import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { WorkshopDto } from '@workshop-hst/shared';
import { api } from '../lib/api';
import { WorkshopCard } from '../components/WorkshopCard';
import { FeaturedWorkshop } from '../components/FeaturedWorkshop';
import { PastListItem } from '../components/PastListItem';
import { Sidebar, type FilterState } from '../components/Sidebar';
import { MobileFilterBar } from '../components/MobileFilterBar';
import { SearchBox } from '../components/SearchBox';
import { groupByMonth } from '../lib/format';

const PAST_PAGE_SIZE = 6;

export function HomePage() {
  const all = useQuery({
    queryKey: ['workshops', 'all'],
    queryFn: () => api.listWorkshops('all'),
  });

  const [filter, setFilter] = useState<FilterState>({ view: 'all', tag: null, search: '' });

  const workshops = all.data?.workshops ?? [];

  const split = useMemo(() => {
    // status is already time-adjusted server-side (effectiveStatus).
    const upcoming = workshops.filter((w) => w.status === 'scheduled' || w.status === 'live');
    const past = workshops.filter((w) => w.status === 'completed');
    upcoming.sort((a, b) => +new Date(a.startsAt) - +new Date(b.startsAt));
    past.sort((a, b) => +new Date(b.startsAt) - +new Date(a.startsAt));
    return { upcoming, past };
  }, [workshops]);

  const tags = useMemo(() => {
    const set = new Set<string>();
    workshops.forEach((w) => w.tags.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [workshops]);

  const filtered = useMemo(() => {
    const matchTag = (w: WorkshopDto) => !filter.tag || w.tags.includes(filter.tag);
    const q = filter.search.trim().toLowerCase();
    const matchSearch = (w: WorkshopDto) =>
      q.length === 0 ||
      w.title.toLowerCase().includes(q) ||
      w.description.toLowerCase().includes(q) ||
      w.tags.some((t) => t.toLowerCase().includes(q));
    return {
      upcoming: split.upcoming.filter((w) => matchTag(w) && matchSearch(w)),
      past: split.past.filter((w) => matchTag(w) && matchSearch(w)),
    };
  }, [split, filter]);

  const counts = {
    all: workshops.length,
    upcoming: split.upcoming.length,
    past: split.past.length,
  };

  const showUpcoming = filter.view === 'all' || filter.view === 'upcoming';
  const showPast = filter.view === 'all' || filter.view === 'past';

  const featured =
    showUpcoming && filter.search.length === 0 && !filter.tag ? filtered.upcoming[0] : undefined;
  const upcomingRest = featured ? filtered.upcoming.slice(1) : filtered.upcoming;

  return (
    <div className="container-page py-8 lg:py-10">
      {/* Page header */}
      <div className="mb-8 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight lg:text-[36px]">
            Workshops
          </h1>
          <p className="mt-1.5 max-w-xl text-sm text-ink-muted">
            Live, hands-on sessions on product, growth, and craft. Register for what's next or catch up on recordings.
          </p>
        </div>
        <SearchBox value={filter.search} onChange={(v) => setFilter({ ...filter, search: v })} />
      </div>

      <MobileFilterBar state={filter} setState={setFilter} counts={counts} tags={tags} />

      <div className="flex gap-10">
        <Sidebar
          state={filter}
          setState={setFilter}
          counts={counts}
          tags={tags}
        />

        <div className="flex-1 min-w-0 space-y-12">
          {all.isLoading ? <LoadingState /> : null}

          {!all.isLoading && featured ? <FeaturedWorkshop workshop={featured} /> : null}

          {showUpcoming && upcomingRest.length > 0 ? (
            <Section
              title={featured ? 'More upcoming' : 'Upcoming'}
              subtitle={`${filtered.upcoming.length} ${filtered.upcoming.length === 1 ? 'workshop' : 'workshops'}`}
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {upcomingRest.map((w) => (
                  <WorkshopCard key={w.id} workshop={w} variant="upcoming" />
                ))}
              </div>
            </Section>
          ) : null}

          {showUpcoming && filtered.upcoming.length === 0 && !all.isLoading ? (
            <EmptyState text="No upcoming workshops match." />
          ) : null}

          {showPast ? <PastSection items={filtered.past} /> : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Past workshops grouped by month with Luma-style "Show more" pagination. Initial
 * limit is small so the home page stays scannable; tapping Show more reveals the
 * next PAST_PAGE_SIZE items. Filter/search changes reset the page.
 */
function PastSection({ items }: { items: WorkshopDto[] }) {
  const [visible, setVisible] = useState(PAST_PAGE_SIZE);

  // Whenever the filter narrows or widens the result set, reset the page so a
  // search doesn't leave you stuck on "Show more" with nothing to show.
  useEffect(() => {
    setVisible(PAST_PAGE_SIZE);
  }, [items]);

  if (items.length === 0) {
    return (
      <Section title="Past workshops" subtitle="0 recordings">
        <EmptyState text="No past workshops match." />
      </Section>
    );
  }

  const slice = items.slice(0, visible);
  const groups = groupByMonth(slice);
  const remaining = items.length - slice.length;

  return (
    <Section
      title="Past workshops"
      subtitle={`${items.length} ${items.length === 1 ? 'recording' : 'recordings'}`}
    >
      <div className="space-y-8">
        {groups.map((group) => (
          <div key={group.monthKey}>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
              {group.monthLabel}
            </h3>
            <div className="flex flex-col gap-2.5">
              {group.items.map((w) => (
                <PastListItem key={w.id} workshop={w} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {remaining > 0 ? (
        <div className="mt-6 flex flex-col items-center gap-1.5">
          <button
            type="button"
            onClick={() => setVisible((n) => n + PAST_PAGE_SIZE)}
            className="btn-secondary"
          >
            Show {Math.min(remaining, PAST_PAGE_SIZE)} more
          </button>
          <span className="text-[11px] text-ink-subtle">
            Showing {slice.length} of {items.length}
          </span>
        </div>
      ) : items.length > PAST_PAGE_SIZE ? (
        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={() => setVisible(PAST_PAGE_SIZE)}
            className="btn-ghost text-xs"
          >
            Collapse
          </button>
        </div>
      ) : null}
    </Section>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {subtitle ? <span className="text-xs text-ink-subtle">{subtitle}</span> : null}
      </div>
      {children}
    </section>
  );
}

function LoadingState() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="card animate-pulse">
          <div className="aspect-[16/10] bg-canvas" />
          <div className="space-y-2 p-5">
            <div className="h-3 w-1/3 rounded bg-canvas" />
            <div className="h-4 w-3/4 rounded bg-canvas" />
            <div className="h-4 w-2/3 rounded bg-canvas" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-line bg-surface p-12 text-center text-sm text-ink-subtle">
      {text}
    </div>
  );
}
